import React, { useState, useEffect } from 'react';
import { Clock, Send, Pause, Play } from 'lucide-react';
import { CampaignTimerConfig } from '../types';

interface CampaignTimerProps {
  config: CampaignTimerConfig;
  onConfigChange: (config: CampaignTimerConfig) => void;
  onPauseResume: () => void;
}

export default function CampaignTimer({ config, onConfigChange, onPauseResume }: CampaignTimerProps) {
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  
  useEffect(() => {
    if (!config.nextSendTime || config.isPaused) return;
    
    const interval = setInterval(() => {
      const now = new Date();
      const diff = config.nextSendTime ? Math.max(0, Math.floor((config.nextSendTime.getTime() - now.getTime()) / 1000)) : 0;
      setTimeLeft(diff);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [config.nextSendTime, config.isPaused]);
  
  // Format time left as mm:ss
  const formatTimeLeft = () => {
    if (timeLeft === null) return '00:00';
    const minutes = Math.floor(timeLeft / 60);
    const seconds = timeLeft % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };
  
  const handleIntervalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value > 0) {
      onConfigChange({
        ...config,
        interval: value
      });
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <h2 className="text-xl font-semibold mb-4 flex items-center">
        <Clock className="h-5 w-5 mr-2 text-blue-500" />
        Campaign Timer
      </h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email-interval">
              Interval between emails (seconds)
            </label>
            <input
              id="email-interval"
              type="number"
              min="1"
              value={config.interval}
              onChange={handleIntervalChange}
              className="w-full px-3 py-2 border rounded-md"
              aria-label="Set interval between emails in seconds"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm text-gray-500 mb-1">Emails Sent</p>
              <p className="text-2xl font-bold text-blue-600">{config.totalSent}</p>
            </div>
            
            <div className="bg-gray-50 p-3 rounded-md">
              <p className="text-sm text-gray-500 mb-1">Remaining</p>
              <p className="text-2xl font-bold text-green-600">{config.totalToSend - config.totalSent}</p>
            </div>
          </div>
          
          <div className="bg-gray-50 p-3 rounded-md">
            <p className="text-sm text-gray-500 mb-1">Next Email In</p>
            <div className="flex justify-between items-center">
              <p className="text-2xl font-bold text-indigo-600">{formatTimeLeft()}</p>
              <button
                onClick={onPauseResume}
                className="bg-white p-2 rounded-full shadow hover:bg-gray-50"
                aria-label={config.isPaused ? "Resume sending emails" : "Pause sending emails"}
              >
                {config.isPaused ? (
                  <Play className="h-5 w-5 text-green-500" />
                ) : (
                  <Pause className="h-5 w-5 text-red-500" />
                )}
              </button>
            </div>
          </div>
          
          {config.startTime && (
            <div className="text-sm text-gray-500">
              Campaign started: {config.startTime.toLocaleTimeString()}
            </div>
          )}
        </div>
        
        <div>
          <h3 className="font-medium text-sm mb-2">Email Preview</h3>
          <div className="border rounded-md p-3 bg-gray-50 max-h-64 overflow-y-auto">
            <div className="bg-white p-3 rounded border mb-2">
              <div className="text-sm text-gray-500 mb-1">Subject: Campaign Update</div>
              <div className="text-sm font-medium">From: {config.emailTemplate.split('\n')[0]}</div>
            </div>
            <div className="text-sm whitespace-pre-line">
              {config.emailTemplate
                .split('\n')
                .slice(1)
                .join('\n')
                .replace(/\{\{recipient\}\}/g, 'example@example.com')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}